import React from "react";
import { useTranslation } from "react-i18next";
import { Star, Quote, MapPin } from "lucide-react";

const TestimonialCard = ({ testimonial }) => {
  const { i18n } = useTranslation();
  const lang = i18n.language;

  // Handle both old (comment/author_name) and new (text/name) data structures
  const text = testimonial.text || testimonial.comment || {};
  const name = testimonial.name || testimonial.author_name || "Voyageur";
  const locationData = testimonial.location || testimonial.country || "";
  const avatar = testimonial.avatar_url || testimonial.image_url;
  const rating = testimonial.rating || 5;

  // Get the text in the current language or fallback
  const displayText = typeof text === "object" 
    ? (text[lang] || text.fr || text.en || "") 
    : text;

  // Get the location in the current language or fallback
  const displayLocation = typeof locationData === "object"
    ? (locationData[lang] || locationData.fr || locationData.en || "")
    : locationData;

  return (
    <div
      className="bg-card rounded-2xl p-6 shadow-md hover:shadow-lg transition-shadow duration-300 flex flex-col h-full"
      data-testid={`testimonial-${testimonial.id}`}
    >
      {/* Quote Icon */}
      <div className="mb-4">
        <Quote className="h-8 w-8 text-primary/30" />
      </div>

      {/* Stars */}
      <div className="flex gap-1 mb-4">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={`h-5 w-5 ${
              i < rating
                ? "text-yellow-400 fill-yellow-400"
                : "text-gray-300"
            }`}
          />
        ))}
      </div>

      {/* Comment */}
      <p className="text-foreground leading-relaxed mb-6 flex-grow line-clamp-4">
        "{displayText}"
      </p>

      {/* Trip info */}
      {(testimonial.trip_type || testimonial.trip_date) && (
        <p className="text-xs text-muted-foreground mb-4">
          {testimonial.trip_type && (
            typeof testimonial.trip_type === "object"
              ? (testimonial.trip_type[lang] || testimonial.trip_type.fr || "")
              : testimonial.trip_type
          )}
          {testimonial.trip_type && testimonial.trip_date && " • "}
          {testimonial.trip_date}
        </p>
      )}

      {/* Author */}
      <div className="flex items-center gap-3 pt-4 border-t border-border">
        {avatar ? (
          <img
            src={avatar}
            alt={name}
            className="w-12 h-12 rounded-full object-cover"
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <span className="text-primary font-semibold text-lg">
              {name.charAt(0)}
            </span>
          </div>
        )}
        <div>
          <p className="font-semibold text-foreground">{name}</p>
          {displayLocation && (
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {displayLocation}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestimonialCard;
